// Pure, dependency-free date formatting. Kept out of ./index (the utils
// barrel) on purpose: importing that barrel eagerly initializes Firebase, so
// unit tests must either mock Firebase or avoid the barrel entirely. Living
// here lets this formatter be tested in isolation with no Firebase mocks.
// ./index re-exports formatDateCT so existing `@util/index` consumers are
// unaffected.

// Date-only sibling of formatTimestampCT (in ./index): listing dates
// ("Listed On", "Last Updated") on the product page. Pinning the time zone to
// America/Chicago means the server (UTC) and the browser (viewer-local) render
// the SAME string, so there is no hydration mismatch near day boundaries and
// no layout shift (unlike a mounted-gate). Output matches the prior
// moment('MMMM DD, YYYY') format, e.g. "July 07, 2026".
export function formatDateCT(timestamp?: number | null): string {
  // Missing/malformed/non-positive timestamps degrade to an empty string;
  // callers supply their own placeholder (the PDP uses an em dash). The
  // `> 0` guard preserves the prior moment() behavior, which treated 0 and
  // non-finite inputs as invalid rather than formatting the Unix epoch.
  if (!Number.isFinite(timestamp) || (timestamp as number) <= 0) return '';
  // Firestore timestamps are milliseconds, but a 10-digit value is seconds;
  // scale it up so both forms format correctly (mirrors formatTimestampCT).
  let ms = timestamp as number;
  if (ms.toString().length === 10) ms *= 1000;
  return new Date(ms).toLocaleString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'America/Chicago',
  });
}

// Marketplace timestamps (orders, sales, payouts) are shown in US Central
// time for every viewer, so buyers, sellers, and support all see the same
// clock on the same order. 'America/Chicago' (not a fixed offset) keeps
// CDT/CST correct across daylight-saving transitions. Lives here (not
// ./index) for the same reason formatDateCT does: it's a pure function that
// needs to be unit-testable without mocking Firebase or the rest of the
// barrel's dependency graph.
export function formatTimestampCT(timestamp: number): string {
  // Malformed order data should degrade to an empty cell, not crash the
  // page. The `> 0` guard (matching formatDateCT's) is required because 0
  // and negative values are numbers that pass a bare NaN check but still
  // aren't a real timestamp -- without it they format as the Unix epoch
  // ("Dec 31, 1969, 6:00 PM CT") instead of degrading like every other
  // invalid input.
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '';
  let ms = timestamp;
  if (ms.toString().length === 10) ms *= 1000;
  return (
    new Date(ms).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: 'America/Chicago',
    }) + ' CT'
  );
}

/**
 * Returns null when there is no usable timestamp, so callers can skip the
 * expiration UI instead of crashing. The `products` collection can hold
 * docs with no date fields at all (GB web#1979: a GearMaster matcher stub
 * with only gearmaster_* fields crashed the admin Listings page).
 */
export function daysUntilExpiration(
  timestamp: number | null | undefined
): number | null {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return null;
  if (timestamp.toString().length === 10) timestamp *= 1000;

  const currentTimestamp = Date.now();
  const timeDifference = currentTimestamp - timestamp;

  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const daysUntil30DaysOld = Math.max(30 - daysDifference, 0);

  return daysUntil30DaysOld;
}
